// Where a parsed row sat in the original file. Prefixed so it can never collide
// with a CSV column, and skipped everywhere columns are inspected.
export const LINE_KEY = '__line'

// Minimal RFC4180-style CSV parser (handles quoted fields with embedded commas/newlines/escaped quotes)
export const parseCsv = text => {
  const rows = []
  let row = []
  let field = ''
  let inQuotes = false

  const pushField = () => {
    row.push(field)
    field = ''
  }
  const pushRow = () => {
    pushField()
    rows.push(row)
    row = []
  }

  const normalized = text.replace(/^﻿/, '') // strip BOM if present

  for (let i = 0; i < normalized.length; i++) {
    const char = normalized[i]
    const next = normalized[i + 1]

    if (inQuotes) {
      if (char === '"' && next === '"') {
        field += '"'
        i++
      } else if (char === '"') {
        inQuotes = false
      } else {
        field += char
      }
    } else if (char === '"') {
      inQuotes = true
    } else if (char === ',') {
      pushField()
    } else if (char === '\r') {
      // ignore, line endings handled on \n
    } else if (char === '\n') {
      pushRow()
    } else {
      field += char
    }
  }
  if (field.length || row.length) {
    pushRow()
  }

  // Blank lines are dropped, but each surviving row remembers the line it came
  // from. Without that, a file with a gap in it would make every later row
  // report an error against the wrong line number.
  const cleanedRows = rows
    .map((cells, index) => ({ cells, line: index + 1 }))
    .filter(({ cells }) => cells.some(cell => cell.trim() !== ''))
  if (!cleanedRows.length) return []

  const headers = cleanedRows[0].cells.map(header => header.trim())
  return cleanedRows.slice(1).map(({ cells, line }) => {
    const record = {}
    headers.forEach((header, index) => {
      record[header] = (cells[index] ?? '').trim()
    })
    record[LINE_KEY] = line
    return record
  })
}

const escapeCsvCell = value => {
  const str = String(value ?? '')
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
}

export const downloadCsv = (filename, rows) => {
  const csvContent = rows.map(row => row.map(escapeCsvCell).join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// Header label for either column shape: tanstack ({header}) or MUI DataGrid ({headerName})
const getExportHeader = col =>
  (typeof col.header === 'string' && col.header) || col.headerName || col.accessorKey || col.field || ''

const formatCsvValue = value => {
  if (value === null || value === undefined) return ''
  if (Array.isArray(value)) return value.map(formatCsvValue).join('; ')
  if (typeof value === 'object') return value.name ?? JSON.stringify(value)
  return value
}

// Action/checkbox columns render only JSX and are skipped, as is any column
// flagged `noExport: true`. A column whose display value isn't a plain row
// field can opt in with `exportValue: row => ...`.
const getExportableColumns = columns => {
  const leafs = []
  const collect = cols => (cols || []).forEach(col => (col.columns ? collect(col.columns) : leafs.push(col)))
  collect(columns)
  return leafs.filter(
    col =>
      col.field !== 'Actions' &&
      col.id !== 'select' &&
      !col.noExport &&
      getExportHeader(col) &&
      (col.exportValue || col.accessorFn || col.valueGetter || col.accessorKey || col.field)
  )
}

const getExportValue = (col, row) => {
  try {
    if (col.exportValue) return col.exportValue(row)
    if (col.accessorFn) return col.accessorFn(row) // tanstack: receives the raw row
    // MUI DataGrid valueGetters here are written against either `params.row` or the row
    // itself, so pass a hybrid object that satisfies both signatures
    if (col.valueGetter) return col.valueGetter({ row, ...row })
    const key = col.accessorKey || col.field
    return key ? row?.[key] : ''
  } catch (err) {
    return ''
  }
}

// Turn table column defs + raw rows into a downloaded CSV file
export const exportTableToCsv = (filename, columns, rows) => {
  const exportColumns = getExportableColumns(columns)
  downloadCsv(filename, [
    exportColumns.map(getExportHeader),
    ...(rows || []).map(row => exportColumns.map(col => formatCsvValue(getExportValue(col, row)))),
  ])
}
